import { useEffect, useState } from "react";
import { UserPlus, Users, X } from "lucide-react";
import { getFamilyDetails } from "@/utils/axios/userUtils.ts";
import { UserType } from "@/utils/userTypes.ts";
import { ShoppingList } from "@/pages/ShoppingList/types.ts";
import { FamilyMember } from "@/features/Login/Family/types.ts";
import AvatarDisplay from "@/features/Login/Profile/AvatarDisplay.tsx";

interface ShareManagementDialogProps {
  item: ShoppingList;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userIds: string[]) => void;
  isLoading: boolean;
  dialogTitle?: string;
}

export function ShareManagementDialog({
  item,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  dialogTitle = "Gestion des partages",
}: ShareManagementDialogProps) {
  // État local pour les IDs des utilisateurs sélectionnés
  const [sharedWithIds, setSharedWithIds] = useState<string[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);

  // Initialiser les IDs des utilisateurs déjà partagés
  useEffect(() => {
    if (item && item.sharedWith) {
      const ids = Array.isArray(item.sharedWith)
        ? item.sharedWith.map((user) => user)
        : [];

      setSharedWithIds(
        ids.filter((id) => typeof id === "string" && id.trim() !== "")
      );
    }
  }, [item]);

  // Charger les membres de la famille quand la modale s'ouvre
  useEffect(() => {
    const loadFamilyMembers = async () => {
      if (!isOpen) return;

      setIsFetchingMembers(true);
      try {
        const family = await getFamilyDetails();
        if (family && family.members) {
          const filteredMembers = family.members.filter(
            (member: UserType) => member._id !== item.owner
          );
          setFamilyMembers(filteredMembers);
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des membres de la famille:",
          error
        );
      } finally {
        setIsFetchingMembers(false);
      }
    };

    loadFamilyMembers();
  }, [isOpen, item.owner]);

  // Ajouter un utilisateur au partage
  const addUser = (userId: string) => {
    if (!sharedWithIds.includes(userId)) {
      setSharedWithIds([...sharedWithIds, userId]);
    }
  };

  // Supprimer un utilisateur du partage
  const removeUser = (userId: string) => {
    setSharedWithIds(sharedWithIds.filter((id) => id !== userId));
  };

  // Sauvegarder les changements
  const handleSave = () => {
    onConfirm(sharedWithIds || []);
  };

  // Trouver les détails d'un utilisateur par ID
  const findUserById = (userId: string): FamilyMember | undefined => {
    return familyMembers.find((member) => member._id === userId);
  };

  if (!isOpen) return null;

  // Filtrer les membres non encore partagés
  const availableMembers = familyMembers.filter(
    (member) => !sharedWithIds.includes(member._id) || member._id !== item.owner
  );

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Users size={20} />
            {dialogTitle}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
            disabled={isLoading}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              {item.name || "Élément"} - Partagé avec:
            </label>
            <div className="border rounded-lg p-2 min-h-24 max-h-48 overflow-y-auto">
              {sharedWithIds.length === 0 ? (
                <div className="text-sm text-gray-500 p-2">
                  Aucun partage pour le moment
                </div>
              ) : (
                <ul className="space-y-2">
                  {sharedWithIds.map((userId) => {
                    const member = findUserById(userId);
                    return (
                      <li
                        key={userId}
                        className="flex justify-between items-center p-2 bg-base-200 rounded-md"
                      >
                        <div className="flex items-center gap-2">
                          <AvatarDisplay
                            avatarUri={member?.avatar}
                            size={40}
                            alt={member?.username}
                            fallbackText={member?.username}
                          />
                          <div>
                            <div className="font-medium">
                              {member?.username}
                            </div>
                            <div className="text-xs text-gray-500">
                              {member?.email}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeUser(userId)}
                          className="btn btn-xs btn-ghost text-error"
                          disabled={isLoading}
                        >
                          <X size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Membres de la famille disponibles:
            </label>
            <div className="border rounded-lg overflow-y-auto max-h-56">
              {isFetchingMembers ? (
                <div className="p-4 text-center">
                  <span className="loading loading-spinner loading-md"></span>
                  <p className="mt-2 text-sm">Chargement des membres...</p>
                </div>
              ) : availableMembers.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  {familyMembers.length === 0
                    ? "Aucun membre trouvé dans votre famille."
                    : "Tous les membres sont déjà dans la liste de partage."}
                </div>
              ) : (
                <ul>
                  {availableMembers.map((member) => (
                    <li
                      key={member._id}
                      className="p-2 hover:bg-base-200 cursor-pointer flex justify-between items-center border-b last:border-0"
                      onClick={() => addUser(member._id)}
                    >
                      <div className="flex items-center gap-2">
                        <AvatarDisplay
                          avatarUri={member.avatar}
                          size={40}
                          alt={member.username}
                          fallbackText={member.username}
                        />
                        <div>
                          <div className="font-medium">{member.username}</div>
                          <div className="text-xs text-gray-500">
                            {member.email}
                          </div>
                        </div>
                      </div>
                      <UserPlus size={16} className="text-primary" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                Sauvegarde...
              </>
            ) : (
              "Enregistrer"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
